import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi, UserInfo } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  login: (code: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserInfo | null>(null);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authApi.checkSession();
      if (response.success && response.authenticated && response.user) {
        setIsAuthenticated(true);
        setUser(response.user);
        return true;
      }
    } catch {
      // Session invalid or expired
    }
    setIsAuthenticated(false);
    setUser(null);
    return false;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      await checkAuth();
      setIsLoading(false);
    };
    initAuth();
  }, [checkAuth]);

  const login = async (loginCode: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authApi.login(loginCode);
      if (response.success && response.user) {
        setIsAuthenticated(true);
        setUser(response.user);
        return { success: true };
      }
      return { success: false, error: response.error || 'Anmeldung fehlgeschlagen' };
    } catch (error: unknown) {
      const err = error as Error;
      return { success: false, error: err.message || 'Anmeldung fehlgeschlagen' };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
